import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import apiConfig from "../../config/apiConfig";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../apis/apiFetch";

import { useRaspStore } from "../../store/raspStore";
import { useGateViewModel } from "../../viewModels/useGateViewModel";
import { LoginContext } from "../../context/LoginContext";
import { validate } from "../../utils/validator";
import { gateValidationRules } from "../../validators/applicantValidator";
import "./FormStyles.css";
import gateScoreCard from "../../images/gate_scorecard_score.png";
import gateScoreCardRank from "../../images/gate_scorecard_rank.png";
import gateScoreCardRegNo from "../../images/gate_score_regno.png";
import RenderImage from "../QueryManager/RenderImage/RenderImage";


export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

// getCookie removed - using apiFetch

export interface GateFormRef {
  save: () => Promise<void>;
  getData: () => any;
  validate: () => Record<string, string>;
}

interface CreateGateProps {
  title?: string;
  allowedSubjects?: string[];
  excludedSubjects?: string[];
  onSubjectChange?: (subject: string) => void;
  preSelectedSubject?: string;
  gateIndex?: number;
  prefix?: string;
  initialData?: any;
}

const CreateGate = forwardRef<GateFormRef, CreateGateProps>((props, ref) => {
  const {
    title,
    allowedSubjects,
    excludedSubjects,
    onSubjectChange,
    preSelectedSubject,
    gateIndex,
    prefix,
    initialData: initialPropData,
  } = props;
  const { user } = useContext(LoginContext); // logged-in user

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [showToast, setShowToast] = useState<any>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const [photoError, setPhotoError] = useState<string>("");
  const [preview, setPreview] = useState<string | null>(null);
  const [showScoreHelp, setShowScoreHelp] = useState(false);
  const [showRankHelp, setShowRankHelp] = useState(false);
  const [showRegNoHelp, setShowRegNoHelp] = useState(false);
  const regex = /^(g_|archived|extra_data)/;
  const apiUrl = apiConfig.getResourceUrl("applications");
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const queryClient = useQueryClient();

  // userId is now passed via props/context from parent or LoginContext

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useGateViewModel(user?.userId || "", appId);

  useEffect(() => {
    loadMetadata();
  }, []);

  // Auto-set user_id from logged-in user's email
  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    const match = users.find((u: any) => {
      const userEmail = (u.email || u.email_id || u.user_email || "")
        .toLowerCase()
        .trim();
      return userEmail === user.email_id.toLowerCase().trim();
    });

    if (match && match.id) {
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
      }));
    }
  }, [user, foreignKeyData]);

  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string,
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  const {
    data: metaData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["resMetaData", "gateCreate"],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter(
        (field: any) => field.foreign,
      );
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ["foreignData", field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(
            field.foreign,
            field.name,
            field.foreign_field,
          );
        }
      }

      const enumFields = data[0].fieldValues.filter(
        (field: any) => field.isEnum === true,
      );
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ["enum", field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  useEffect(() => {
    // If a preSelectedSubject is passed (e.g., from parent logic), update state
    if (preSelectedSubject && dataToSave["subjects"] !== preSelectedSubject) {
      setDataToSave((prev: any) => ({ ...prev, subjects: preSelectedSubject }));
    }
  }, [preSelectedSubject]);

  useEffect(() => {
    console.log("data to save", dataToSave);
    if (onSubjectChange && dataToSave["subjects"]) {
      onSubjectChange(dataToSave["subjects"]);
    }
  }, [dataToSave]);

  const {
    data: dataRes,
    isLoading: isLoadingDataRes,
    error: errorDataRes,
  } = useQuery({
    queryKey: ["resourceData", "gateCreate", user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId
  });

  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (dataRes && activeDriveId) {
      const activeRecord = dataRes.find((r: any) => r.drive_id === activeDriveId);
      let dataToStore = { resource: "gate", records: activeRecord ? [activeRecord] : [] };
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user]);

  // Pre-populate form with existing data when user_id is resolved and records are loaded or prefix changes
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (!activeDriveId || !dataRes) return;

    const userRecords = dataRes.filter((r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId);
    const indexToUse = gateIndex !== undefined ? gateIndex : 0;
    const userRecord = userRecords.length > indexToUse ? userRecords[indexToUse] : null;

    // Source of truth: priority given to parent's initialPropData (shuffled state)
    // falling back to raw fetched records if available.
    const dataSource = initialPropData || userRecord;

    if (dataSource && prefix) {
      // If we are sliding, we always want to pick up the data from the new prefix
      // Even if hasPrePopulated is true, a prefix change means data has moved.

      let initialData: any = {};
      
      // Map prefixed fields (e.g., pref1rollno) to flat fields (e.g., rollno)
      if (dataSource[`${prefix}rollno`]) initialData.rollno = dataSource[`${prefix}rollno`];
      if (dataSource[`${prefix}score`]) initialData.score = dataSource[`${prefix}score`];
      if (dataSource[`${prefix}rank`]) initialData.rank = dataSource[`${prefix}rank`];
      if (dataSource[`${prefix}subjects`]) initialData.subjects = dataSource[`${prefix}subjects`];
      if (dataSource[`${prefix}year`]) initialData.year = dataSource[`${prefix}year`];
      if (dataSource[`${prefix}scorecard`]) initialData.scorecard = dataSource[`${prefix}scorecard`];

      // Merge initialData into local dataToSave
      setDataToSave((prev: any) => ({ ...prev, ...initialData }));

      if (Object.keys(initialData).length > 0) {
        hasPrePopulated.current = true;
      }
    }
  }, [dataRes, dataToSave.user_id, gateIndex, prefix, initialPropData]);

  const handleCreate = async () => {
    await save();
    queryClient.invalidateQueries({ queryKey: ["resourceData", "gateCreate"] });
    // setDataToSave({});
  };

  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getData: () => dataToSave,
    validate: () => {
      const vErrors = validate(dataToSave, gateValidationRules);
      setErrors(vErrors);
      return vErrors;
    }
  }));

  const handleSearchChange = (fieldName: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [fieldName]: value }));
  };

  return (
    <div className="form-container">
      {/* <h3 className="form-subtitle">{title || "GATE Exam Details"}</h3> */}

      <div className="form-grid">
        {/* Row 1 */}
        <div className="form-group">
          <label className="form-label">
            GATE Subject *{" "}
            {fields.find((f: any) => f.name === "subjects")?.required === "true"
              ? "*"
              : ""}
          </label>
          <select
            className={`form-input ${errors.subjects ? "input-error" : ""}`}
            name="subjects"
            required={
              fields.find((f: any) => f.name === "subjects")?.required ===
              "true"
            }
            value={dataToSave["subjects"] || ""}
            onChange={(e) => {
              setDataToSave({ ...dataToSave, subjects: e.target.value });
              if (errors.subjects) setErrors(prev => ({ ...prev, subjects: "" }));
            }}
          >
            <option value="">Select Subject</option>
            {enums["Gatesubject"]?.map((option: string, idx: number) => {
              // Logic:
              // 1. If allowedSubjects exists, only show if in allowedSubjects
              // 2. If excludedSubjects exists and option is in it AND option is not the currently selected one, disable it

              if (
                allowedSubjects &&
                allowedSubjects.length > 0 &&
                !allowedSubjects.includes(option)
              ) {
                return null;
              }

              const isDisabled =
                excludedSubjects?.includes(option) &&
                dataToSave["subjects"] !== option;

              return (
                <option
                  key={idx}
                  value={option}
                  disabled={isDisabled}
                  style={
                    isDisabled ? { color: "#999", fontStyle: "italic" } : {}
                  }
                >
                  {option} {isDisabled ? "(Already Selected)" : ""}
                </option>
              );
            })}
          </select>
          {errors.subjects && <span className="error-text">{errors.subjects}</span>}
        </div>

        <div className="form-group"
          onMouseEnter={() => setShowRegNoHelp(true)}
          onMouseLeave={() => setShowRegNoHelp(false)}
          style={{ position: "relative" }}>
          <label className="form-label">Registration Number *</label>
          <input
            type="text"
            className={`form-input ${errors.rollno ? "input-error" : ""}`}
            name="rollno"
            required
            value={dataToSave["rollno"] || ""}
            placeholder="Registration Number"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, rollno: e.target.value });
              if (errors.rollno) setErrors(prev => ({ ...prev, rollno: "" }));
            }}
          />
          {errors.rollno && <span className="error-text" style={{ bottom: "-15px" }}>{errors.rollno}</span>}


          {showRegNoHelp && (
            <div className="score-hover-preview">
              <div className="score-hover-header">
                Please Enter the below <strong>GATE Registration Number</strong> from your official scorecard.
              </div>

              <img
                src={gateScoreCardRegNo}
                alt="Sample GATE Scorecard"
                className="score-hover-image"
              />
            </div>
          )}
        </div>

        <div
          className="form-group"
          onMouseEnter={() => setShowScoreHelp(true)}
          onMouseLeave={() => setShowScoreHelp(false)}
          style={{ position: "relative" }}
        >
          <label className="form-label">GATE Score *</label>

          <input
            type="number"
            className={`form-input ${errors.score ? "input-error" : ""}`}
            name="score"
            required
            min={100}
            max={1000}
            value={dataToSave["score"] || ""}
            placeholder="Score"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, score: e.target.value });
              if (errors.score) setErrors(prev => ({ ...prev, score: "" }));
            }}
          />
          {errors.score && <span className="error-text" style={{ bottom: "-15px" }}>{errors.score}</span>}

          {dataToSave["score"] !== "" && Number(dataToSave["score"]) < 100 && (
            <span style={{ color: "red", fontSize: "0.8rem" }}>
              Minimum score required is 100
            </span>
          )}

          {dataToSave["score"] !== "" && Number(dataToSave["score"]) > 1000 && (
            <span style={{ color: "red", fontSize: "0.8rem" }}>
              Invalid Score
            </span>
          )}

          {/* Hover Image */}
          {showScoreHelp && (
            <div className="score-hover-preview">
              <div className="score-hover-header">
                Please Enter the below <strong>GATE Score</strong> from your official scorecard.
              </div>
              {/* <div className="score-hover-subtext">
                Please refer to your official GATE scorecard and enter the
                <strong> Normalized Score </strong> shown below.
              </div> */}

              <img
                src={gateScoreCard}
                alt="Sample GATE Scorecard"
                className="score-hover-image"
              />
            </div>
          )}
        </div>
        {/* Row 2 */}
        <div
          className="form-group"
          onMouseEnter={() => setShowRankHelp(true)}
          onMouseLeave={() => setShowRankHelp(false)}
          style={{ position: "relative" }}
        >
          <label className="form-label">Rank *</label>

          <input
            type="text"
            className={`form-input ${errors.rank ? "input-error" : ""}`}
            name="rank"
            required
            value={dataToSave["rank"] || ""}
            placeholder="All India Rank"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, rank: e.target.value });
              if (errors.rank) setErrors(prev => ({ ...prev, rank: "" }));
            }}
          />
          {errors.rank && <span className="error-text" style={{ bottom: "-15px" }}>{errors.rank}</span>}

          {showRankHelp && (
            <div className="score-hover-preview">
              <div className="score-hover-header">
                Please Enter the below <strong>GATE Rank</strong> from your official scorecard.
              </div>

              <img
                src={gateScoreCardRank}
                alt="Sample GATE Scorecard"
                className="score-hover-image"
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <>
            <label className="form-label">Scorecard *</label>
            <div className="file-upload-wrapper">
              <input
                className={`form-input file-input ${errors.scorecard ? "input-error" : ""}`}
                type="file"
                name={`${prefix}scorecard`}
                accept="image/jpeg,image/png,image/jpg, application/pdf"
                required
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && file.size > 512 * 1024) {
                    setPhotoError("Photo size must be less than 512 KB. Please choose a smaller file.");
                    e.target.value = "";
                    // setDataToSave({ ...dataToSave, photo: null });
                    return;
                  }
                  setPhotoError("");
                  setDataToSave({
                    ...dataToSave,
                    scorecard: e.target.files?.[0] || null,
                  });
                  if (e.target.files?.[0]) {
                    const objectUrl = URL.createObjectURL(e.target.files?.[0]);
                    setPreview(objectUrl);
                  }
                  if (errors.scorecard) setErrors(prev => ({ ...prev, scorecard: "" }));
                }}
              />
            </div>
            {photoError && <span style={{ color: "red", fontSize: "0.8rem", marginTop: "4px", display: "block" }}>{photoError}</span>}
            {errors.scorecard && <span className="error-text">{errors.scorecard}</span>}
            <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
              Max size: 512 KB | Resolution: e.g. 200x200 pixels | Formats: JPG, JPEG, PNG, PDF
            </small>
          </>
          {preview ?
            <div style={{ display: "flex", justifyContent: "flex-start", marginLeft: 100, marginTop: 10 }}>
              <img src={preview} alt="preview" style={{ maxWidth: '40%' }} />
            </div> :
            <div>
              <small style={{ color: "#1d1e1f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Previous Uploaded File:
              </small>
              <RenderImage
                photo={dataToSave["scorecard"]}
              />
            </div>
          }
        </div>
        <div className="form-group">
          <label className="form-label">Year *</label>
          <input
            type="text"
            className={`form-input ${errors.year ? "input-error" : ""}`}
            name="year"
            required
            value={dataToSave["year"] || ""}
            placeholder="Year of Exam (YYYY)"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, year: e.target.value });
              if (errors.year) setErrors(prev => ({ ...prev, year: "" }));
            }}
          />
          {errors.year && <span className="error-text">{errors.year}</span>}
        </div>
      </div>
    </div >
  );
});

export default CreateGate;
